import React, { useRef, useState } from 'react';
import {
  Copy,
  LayoutGrid,
  MoreVertical,
  Presentation,
  RefreshCw,
  Send,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import AorApprovalSettingsDialog from 'components/Reports/AorApprovalSettingsDialog';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import type { ReportsLayoutMode } from 'components/Reports/hooks/useReportsLayoutMode';
import SitrepAutoRefreshControls, {
  type SitrepAutoRefreshControlsHandle,
} from 'components/Reports/SitrepAutoRefreshControls';
import ReportExportMenuItems from 'components/Reports/ReportsExportButton';
import SitrepLayoutMenuItems from 'components/Reports/SitrepLayoutMenuItems';
import {
  useDuplicateSitrepAsDraftV2,
  useRemoveSitrepV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { usePublishAorReport } from 'components/Reports/hooks/useAorReportApprovalPolicy';
import { useReportVersioningFlag } from 'utils/featureFlags';

type SitrepCardActionsMenuProps = {
  layoutMode: ReportsLayoutMode;
  onLayoutModeChange: (mode: ReportsLayoutMode) => void;
  /** When set, authorized users can retire this Current report to Historical. */
  retireSitrep?: GetSitrepsResponse;
  /** Report this menu belongs to. AOR reports can edit the area approval policy. */
  sitrep?: GetSitrepsResponse;
  onPresent?: () => void;
  onQuadChart?: () => void;
  presentDisabled?: boolean;
  /**
   * Current reports can draft a new version and set auto-refresh from this
   * menu. Historical cards leave both off.
   */
  showVersionActions?: boolean;
  hasInProgressDraft?: boolean;
};

/** Kebab with layout radios for current, historical, and review report cards. */
const SitrepCardActionsMenu: React.FC<SitrepCardActionsMenuProps> = ({
  layoutMode,
  onLayoutModeChange,
  retireSitrep,
  sitrep,
  onPresent,
  onQuadChart,
  presentDisabled = false,
  showVersionActions = false,
  hasInProgressDraft = false,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showApprovalSettings, setShowApprovalSettings] = useState(false);
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  const [showRefreshInterval, setShowRefreshInterval] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [refreshSaving, setRefreshSaving] = useState(false);
  const refreshControlsRef = useRef<SitrepAutoRefreshControlsHandle>(null);
  const reportVersioning = useReportVersioningFlag();
  const duplicate = useDuplicateSitrepAsDraftV2();
  const approvalSitrep = sitrep ?? retireSitrep;
  const isAorReport = !!approvalSitrep?.aor_id && !approvalSitrep.incident_id;
  const canPublishIncident =
    !!approvalSitrep?.incident_id &&
    (approvalSitrep.approval_status === 'PENDING' ||
      approvalSitrep.approval_status === 'DRAFT' ||
      approvalSitrep.approval_status === 'REJECTED');
  const publishReport = usePublishAorReport();
  const removeSitrep = useRemoveSitrepV2();
  const reportLabel = retireSitrep?.name?.trim() || 'this report';
  const versionSitrep = sitrep ?? retireSitrep;
  const draftBlockedMessage = versionSitrep?.incident_id
    ? 'This incident already has a draft.'
    : 'This area already has a draft.';

  const handleConfirmDraft = async () => {
    if (!versionSitrep) return;
    try {
      await duplicate.mutateAsync({ sitrepId: versionSitrep.id });
      setShowDraftConfirm(false);
    } catch (error) {
      const message = (error as { response?: { data?: { error?: unknown } } })
        .response?.data?.error;
      toast.error(
        typeof message === 'string'
          ? message
          : 'Failed to create the draft report.'
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!retireSitrep) return;
    try {
      await removeSitrep.mutateAsync({ sitrepId: retireSitrep.id });
      setShowDeleteConfirm(false);
      toast.success('Report moved to Historical');
    } catch {
      toast.error('Failed to delete report');
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Report actions"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreVertical className="tw:h-4 tw:w-4" />
            </Button>
          }
        />
        <DropdownMenuContent
          align="end"
          className="dark tw:w-64"
          onClick={(event) => event.stopPropagation()}
        >
          <SitrepLayoutMenuItems
            layoutMode={layoutMode}
            onLayoutModeChange={onLayoutModeChange}
          />
          {onPresent ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={presentDisabled} onClick={onPresent}>
                <Presentation />
                Presentation mode
              </DropdownMenuItem>
              {onQuadChart ? (
                <DropdownMenuItem
                  disabled={presentDisabled}
                  onClick={onQuadChart}
                >
                  <LayoutGrid />
                  Quad Chart
                </DropdownMenuItem>
              ) : null}
            </>
          ) : null}
          {approvalSitrep ? (
            <>
              <DropdownMenuSeparator />
              <ReportExportMenuItems sitrep={approvalSitrep} />
            </>
          ) : null}
          {canPublishIncident ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={publishReport.isPending}
                onClick={() => setShowPublishConfirm(true)}
              >
                <Send />
                Publish
              </DropdownMenuItem>
            </>
          ) : null}
          {isAorReport && approvalSitrep?.aor_id ? (
            <>
              <DropdownMenuSeparator />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span className="tw:block">
                        <DropdownMenuItem
                          disabled
                          className="tw:pointer-events-none"
                        >
                          <ShieldCheck />
                          Approval settings
                        </DropdownMenuItem>
                      </span>
                    }
                  />
                  <TooltipContent className="dark">
                    Upgrade to unlock approval workflows.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          ) : null}
          {showVersionActions && versionSitrep ? (
            <>
              <DropdownMenuSeparator />
              {reportVersioning ? null : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="tw:block">
                          <DropdownMenuItem
                            disabled={duplicate.isPending || hasInProgressDraft}
                            className={
                              hasInProgressDraft
                                ? 'tw:pointer-events-none'
                                : undefined
                            }
                            onClick={() => setShowDraftConfirm(true)}
                          >
                            <Copy />
                            Draft New Version
                          </DropdownMenuItem>
                        </span>
                      }
                    />
                    {hasInProgressDraft ? (
                      <TooltipContent className="dark">
                        {draftBlockedMessage}
                      </TooltipContent>
                    ) : null}
                  </Tooltip>
                </TooltipProvider>
              )}
              <DropdownMenuItem onClick={() => setShowRefreshInterval(true)}>
                <RefreshCw />
                Set Refresh Time Interval
              </DropdownMenuItem>
            </>
          ) : null}
          {retireSitrep ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                disabled={removeSitrep.isPending}
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 />
                Delete report
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={showPublishConfirm}
        onOpenChange={(open) => {
          if (publishReport.isPending) return;
          setShowPublishConfirm(open);
        }}
      >
        <DialogContent className="dark tw:sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Publish this report?</DialogTitle>
            <DialogDescription>
              This report becomes the current report. No approval is required.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={publishReport.isPending}
              onClick={() => setShowPublishConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={publishReport.isPending || !approvalSitrep}
              onClick={() => {
                if (!approvalSitrep) return;
                void publishReport
                  .mutateAsync(approvalSitrep.id)
                  .then(() => {
                    setShowPublishConfirm(false);
                    toast.success('Published as the current report');
                  })
                  .catch((error: { response?: { data?: { error?: unknown } } }) => {
                    const serverMessage = error.response?.data?.error;
                    toast.error(
                      typeof serverMessage === 'string'
                        ? serverMessage
                        : 'Failed to publish report'
                    );
                  });
              }}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {isAorReport && approvalSitrep?.aor_id ? (
        <AorApprovalSettingsDialog
          open={showApprovalSettings}
          aorId={approvalSitrep.aor_id}
          onOpenChange={setShowApprovalSettings}
        />
      ) : null}
      {showVersionActions && versionSitrep ? (
        <>
          <Dialog
            open={showDraftConfirm}
            onOpenChange={(open) => {
              if (duplicate.isPending) return;
              setShowDraftConfirm(open);
            }}
          >
            <DialogContent
              className="dark tw:z-[1101] tw:sm:max-w-lg"
              overlayClassName="tw:z-[1100]"
            >
              <DialogHeader>
                <DialogTitle>Draft a new version?</DialogTitle>
                <DialogDescription>
                  A new draft is created from this report and opens in Drafts.
                  The current report stays published.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={duplicate.isPending}
                  onClick={() => setShowDraftConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={duplicate.isPending}
                  onClick={() => {
                    void handleConfirmDraft();
                  }}
                >
                  Draft New Version
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog
            open={showRefreshInterval}
            onOpenChange={(open) => {
              if (refreshSaving) return;
              setShowRefreshInterval(open);
            }}
          >
            <DialogContent
              className="dark tw:z-[1101] tw:sm:max-w-lg"
              overlayClassName="tw:z-[1100]"
            >
              <DialogHeader>
                <DialogTitle>Set Refresh Time Interval</DialogTitle>
                <DialogDescription>
                  Choose how often this report should refresh its draft, and who
                  to notify when that draft is updated.
                </DialogDescription>
              </DialogHeader>
              {showRefreshInterval ? (
                <SitrepAutoRefreshControls
                  ref={refreshControlsRef}
                  sitrep={versionSitrep}
                  persistOnChange={false}
                />
              ) : null}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={refreshSaving}
                  onClick={() => setShowRefreshInterval(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={refreshSaving}
                  onClick={async () => {
                    setRefreshSaving(true);
                    const saved = await refreshControlsRef.current?.save();
                    setRefreshSaving(false);
                    if (saved) setShowRefreshInterval(false);
                  }}
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
      {retireSitrep ? (
        <ConfirmDeleteDialog
          open={showDeleteConfirm}
          title="Delete current report?"
          confirmLabel="Delete"
          pending={removeSitrep.isPending}
          onOpenChange={(open) => {
            if (!open && !removeSitrep.isPending) setShowDeleteConfirm(false);
          }}
          onConfirm={() => {
            void handleConfirmDelete();
          }}
          description={
            <>
              Delete &ldquo;{reportLabel}&rdquo; from Current? It will be kept
              under Historical so you can still open it. If another published
              report exists for this area, that report becomes Current.
            </>
          }
        />
      ) : null}
    </>
  );
};

export default SitrepCardActionsMenu;
